import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { Download, Expand, Eye, FileDown, Loader2, Printer, RefreshCw, X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../../lib/axios";
import { useAuth } from "../../../context/AuthContext";
import { canAccessFinanceModule } from "../../../utils/roleAccess";
import FinanceAccessDenied from "../../../components/finance/FinanceAccessDenied";
import FinanceApiErrorBanner from "../../../components/finance/FinanceApiErrorBanner";
import { printPdfUrl } from "../../../utils/printDocument";
import {
  AdminDataTableShell,
  AdminEmptyState,
  AdminPageHero,
  adminHeroActionClassName,
} from "../../../modules/admin/ui";

type QuittanceRow = {
  id: number;
  numero: string;
  type?: "FULL_RECEIPT" | "DEPOSIT_RECEIPT";
  label?: string;
  amountPaid?: number;
  remainingBalance?: number;
  currency?: string;
  moyenPaiement?: string | null;
  statut?: string;
  datePaiement?: string | null;
  createdAt: string;
  paiement?: {
    id: number;
    reference: string | null;
    montant: number;
    devise: string;
    modePaiement: string | null;
    datePaiement: string | null;
    facture?: {
      id: number;
      numero: string | null;
      montantTTC?: number | null;
      resteAPayer?: number | null;
      client?: {
        prenom?: string | null;
        nom?: string | null;
        raisonSociale?: string | null;
        nomEntreprise?: string | null;
        telephone?: string | null;
      } | null;
    } | null;
  } | null;
};

export default function FinanceQuittancesPage(): React.JSX.Element {
  const { user, isLoading: authLoading } = useAuth();

  const [items, setItems] = useState<QuittanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>("Quittance");

  const load = async () => {
    setForbidden(false);
    setServerError(null);
    setLoading(true);
    try {
      const res = await api.get<{ items?: QuittanceRow[] }>("/paiements/quittances", {
        params: { page: 1, limit: 100, search: search.trim() || undefined },
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setForbidden(true);
        setItems([]);
        return;
      }
      if (isAxiosError(e) && e.response?.status === 500) {
        const msg =
          (e.response?.data as { message?: string })?.message ||
          "Impossible de charger les quittances.";
        setServerError(msg);
        setItems([]);
        return;
      }
      console.error(e);
      toast.error("Erreur chargement quittances.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = window.setTimeout(() => void load(), search ? 400 : 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const getPdfBlobUrl = async (paiementId: number): Promise<string> => {
    const res = await api.get(`/paiements/${paiementId}/pdf`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    return window.URL.createObjectURL(blob);
  };

  const openPdf = async (paiementId: number, title: string) => {
    try {
      const url = await getPdfBlobUrl(paiementId);
      setPreviewTitle(title);
      setPreviewUrl(url);
    } catch {
      toast.error("Impossible d’ouvrir le document.");
    }
  };

  const downloadPdf = async (paiementId: number, title: string) => {
    try {
      const url = await getPdfBlobUrl(paiementId);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.toLowerCase().replace(/\s+/g, "-")}.pdf`;
      a.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Téléchargement impossible.");
    }
  };

  const printPdf = async (paiementId: number) => {
    try {
      const url = await getPdfBlobUrl(paiementId);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        toast.warning("Autorisez les popups pour imprimer le document.");
        window.URL.revokeObjectURL(url);
        return;
      }
      win.addEventListener("load", () => win.print(), { once: true });
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Impression impossible.");
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      </div>
    );
  }

  if (!user || !canAccessFinanceModule(user.role)) {
    return <FinanceAccessDenied />;
  }

  if (forbidden) {
    return <FinanceAccessDenied title="Accès refusé" detail="Liste des quittances refusée (403)." />;
  }

  return (
    <div className="flex w-full min-w-0 max-w-none flex-col gap-6">
      {previewUrl ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-slate-700 bg-slate-900 px-4 py-3">
            <p className="font-semibold text-white">Aperçu {previewTitle}</p>
            <div className="flex flex-wrap gap-2">
              <a
                href={previewUrl}
                download={`${previewTitle.toLowerCase().replace(/\s+/g, "-")}.pdf`}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-500"
              >
                <Download className="h-4 w-4" />
                Télécharger PDF
              </a>
              <button
                type="button"
                onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
              >
                <Expand className="h-4 w-4" />
                Plein écran
              </button>
              <button
                type="button"
                onClick={() => void printPdfUrl(previewUrl).catch(() => toast.error("Impression impossible."))}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
              >
                <Printer className="h-4 w-4" />
                Imprimer
              </button>
              <button
                type="button"
                onClick={closePreview}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
                Fermer
              </button>
            </div>
          </div>
          <iframe title={previewTitle} src={previewUrl} className="min-h-0 flex-1 rounded-2xl bg-white" />
        </div>
      ) : null}

      <AdminPageHero
        eyebrow="Finance"
        title="Quittances & reçus"
        subtitle="Documents générés après paiement : quittances pour règlement total, reçus d’acompte pour paiement partiel."
        actions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              className={adminHeroActionClassName(false)}
            >
              <RefreshCw className={`mr-2 inline h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <Link to="/admin/paiements" className={adminHeroActionClassName(false)}>
              Paiements
            </Link>
          </>
        }
      />

      {serverError ? <FinanceApiErrorBanner message={serverError} onRetry={() => void load()} /> : null}

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Rechercher n°, facture, client…"
        className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-sm text-white placeholder:text-slate-500"
      />

      <AdminDataTableShell>
        {loading ? (
          <div className="flex justify-center py-16 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <AdminEmptyState
            title="Aucune quittance ou reçu"
            description="Les documents apparaissent uniquement après un paiement réel enregistré côté serveur."
            action={
              <Link
                to="/admin/paiements"
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500"
              >
                Voir les paiements
              </Link>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="border-b border-slate-800 bg-slate-950/80 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Numéro reçu</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Facture</th>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Date paiement</th>
                  <th className="px-4 py-3 text-right">Montant payé</th>
                  <th className="px-4 py-3 text-right">Solde restant</th>
                  <th className="px-4 py-3">Mode paiement</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => {
                  const p = q.paiement;
                  const cl = p?.facture?.client;
                  const nom =
                    cl?.nomEntreprise?.trim() ||
                    cl?.raisonSociale?.trim() ||
                    [cl?.prenom, cl?.nom].filter(Boolean).join(" ").trim() ||
                    "—";
                  const title =
                    q.label ||
                    (q.type === "FULL_RECEIPT" ? "QUITTANCE DE PAIEMENT" : "REÇU D’ACOMPTE");
                  const currency = q.currency || String(p?.devise || "");
                  return (
                    <tr key={q.id} className="border-b border-slate-800/80 hover:bg-slate-800/40">
                      <td className="px-4 py-3 font-mono text-xs text-white">{q.numero}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-slate-700/70 px-2.5 py-0.5 text-xs font-medium text-slate-100 ring-1 ring-slate-600">
                          {title}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{p?.facture?.numero ?? "—"}</td>
                      <td className="px-4 py-3">{nom}</td>
                      <td className="px-4 py-3 text-slate-400">
                        {p?.datePaiement
                          ? dayjs(p.datePaiement).format("DD/MM/YYYY HH:mm")
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {Number(q.amountPaid ?? p?.montant ?? 0).toLocaleString("fr-FR")} {currency}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {Number(q.remainingBalance ?? 0).toLocaleString("fr-FR")} {currency}
                      </td>
                      <td className="px-4 py-3">{q.moyenPaiement ?? p?.modePaiement ?? "—"}</td>
                      <td className="px-4 py-3">{q.statut ?? "VALIDE"}</td>
                      <td className="px-4 py-3 text-right">
                        {p?.id ? (
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => void openPdf(p.id, q.numero)}
                              className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300"
                            >
                              <Eye className="h-4 w-4" />
                              Voir
                            </button>
                            <button
                              type="button"
                              onClick={() => void downloadPdf(p.id, q.numero)}
                              className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                            >
                              <FileDown className="h-4 w-4" />
                              Télécharger PDF
                            </button>
                            <button
                              type="button"
                              onClick={() => void printPdf(p.id)}
                              className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"
                            >
                              <Printer className="h-4 w-4" />
                              Imprimer
                            </button>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </AdminDataTableShell>
    </div>
  );
}
